/**
 * The Visit calendar chip's hover lines: when it happens, its approval (why the chip is dashed or solid), why it is
 * coloured, its Job beside the status, and its crew. The heading
 * already names the line and status and the chip shows the site, so these lines never repeat them.
 */
import { resolveVisitZone, VisitBrowseRow } from '@jms/model';
import type { EntityCalendarTooltipLine } from '@workbuddy/services/entities';
import { DateTime } from 'luxon';
import { visitAgeTag } from '../presentation/visit.age.tag';
import { visitApprovalTag } from '../presentation/visit.approval.tag';
import { visitConstructionTag } from '../presentation/visit.construction.tag';

/** Called by the Visit manifest's calendar for each chip's tooltip. */
export function visitCalendarTooltip(row: VisitBrowseRow): readonly EntityCalendarTooltipLine[] {

  const lines: EntityCalendarTooltipLine[] = [];
  const when = visitWhen(row);
  if (when) lines.push({ icon: 'fa-regular fa-clock', text: when });
  // Null once a Job exists: the Job is the commitment then, and the chip is solid whatever the approval said.
  // Beside the status on the heading, in the approval pill's colour: the second status a dashed chip is about.
  const approval = visitApprovalTag(null, row);
  if (approval?.label) lines.push({ icon: 'fa-regular fa-stamp', text: approval.tooltip ?? approval.label, color: approval.color, heading: true });
  // The same pill that coloured the chip, in its colour, so the hover answers "why is this one red".
  const attention = visitConstructionTag(null, row) ?? visitAgeTag(null, row);
  if (attention?.label) lines.push({ icon: 'fa-regular fa-triangle-exclamation', text: attention.label, color: attention.color });
  const crew = [row.contractor, ...(row.resources ?? [])].map(person => person?.name).filter((name): name is string => !!name);
  // The Job takes the heading slot approval held before it existed; people keep their own line.
  if (row.job?.name) lines.push({ icon: 'fa-regular fa-briefcase', text: row.job.name, heading: true });
  if (crew.length) lines.push({ icon: 'fa-regular fa-user', text: crew.join(', ') });
  return lines;
}

/** "Tue 4 Nov · 08:00–12:00", or "· Any time" for an all-day Visit, in the Visit's own zone. */
function visitWhen(row: VisitBrowseRow): string | null {

  const zone = resolveVisitZone(row.timezone);
  const start = DateTime.fromJSDate(new Date(row.startDate), { zone });
  const end = DateTime.fromJSDate(new Date(row.endDate), { zone });
  if (!start.isValid) return null;
  const day = start.toFormat('ccc d LLL');
  if (row.allDay || !end.isValid) return `${day} · Any time`;
  return `${day} · ${start.toFormat('HH:mm')}–${end.toFormat('HH:mm')}`;
}
